import React, { useEffect, useMemo, useState } from "react";
import {
  CHAT_STATUSES,
  isChatAdminIdentity,
  isAnnouncementChannelConversation,
  isClownProfile,
  isHelperModeratorProfile,
  isModeratorProfile,
  MAX_GROUP_MEMBERS,
  messageMentionsProfile,
  tsMs,
  xpLevelInfoForProfile,
  xpPointsForProfile,
  xpRewardForProfile,
} from "./chatService";
import { avatarColor, avatarInitial } from "./avatar";

const NAME_LOGIN_DOMAIN = "vmcreation.chat";

function relativeTime(ts) {
  const ms = tsMs(ts);
  if (!ms) return "";
  const d = Date.now() - ms;
  if (d < 60_000) return "now";
  if (d < 3600_000) return Math.floor(d / 60_000) + "m";
  if (d < 86400_000) return Math.floor(d / 3600_000) + "h";
  return Math.floor(d / 86400_000) + "d";
}

function display(c, meUid, profilesByUid) {
  if (isAnnouncementChannelConversation(c)) {
    const title = c.name || "Announcements & Rules";
    return {
      title: `# ${title}`,
      subtitle: c.serverName ? `${c.serverName} • read only` : "Read only",
      avatarText: "#",
      avatarBg: avatarColor(`server:${c.serverId || c.id}`),
    };
  }
  if (c.type === "group") {
    const title = c.name || "Untitled group";
    return {
      title,
      subtitle: `${c.memberIds.length} members`,
      avatarText: avatarInitial(title),
      avatarBg: avatarColor("group:" + c.id),
    };
  }
  const otherUid = c.memberIds.find((id) => id !== meUid);
  const other = otherUid ? profilesByUid[otherUid] : null;
  const name = other ? other.displayName : "Unknown user";
  const status = effectiveStatus(other);
  return {
    title: name,
    subtitle: status === "invisible" ? "Offline" : CHAT_STATUSES[status]?.label || "",
    avatarText: avatarInitial(name),
    avatarBg: avatarColor(otherUid || ""),
    status,
  };
}

function effectiveStatus(profile) {
  if (!profile) return "invisible";
  return CHAT_STATUSES[profile.status] ? profile.status : "invisible";
}

function menuMemberDetails(uid, me, profilesByUid) {
  const profile = uid === me.uid ? me : profilesByUid[uid];
  const displayName = profile?.displayName || profile?.loginName || "Unknown user";
  const email = profile?.email || (profile?.loginName ? `${profile.loginName}@${NAME_LOGIN_DOMAIN}` : "No email on profile");
  const isAdmin = isChatAdminIdentity(profile || {});
  const isHelperModerator = !isAdmin && isHelperModeratorProfile(profile || {});
  const isModerator = !isAdmin && isModeratorProfile(profile || {});
  const isFullModerator = isModerator && !isHelperModerator;
  const isClown = !isAdmin && isClownProfile(profile || {});
  return {
    uid,
    displayName,
    email,
    isMe: uid === me.uid,
    isAdmin,
    isHelperModerator,
    isFullModerator,
    isModerator,
    isClown,
  };
}

export default function ChatSidebar({
  me,
  conversationGroups,
  sectionLabel = "Chats",
  conversationPrefsById,
  profilesByUid,
  activeId,
  onPick,
  onNew,
  onAddPeople,
  onRemoveMember,
  onLeaveGroup,
  onTransferOwnership,
  onArchiveConversation,
  onHideConversation,
  onSignOut,
  statusMode,
  canAdminManageGroups,
  canModerateMembers,
  canAssignModerators,
  onSetMemberRole,
  onSetClownRole,
  newChatDisabled,
  newChatTitle,
  showCollapseControl,
  onCollapseSidebar,
}) {
  const [menu, setMenu] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ archived: false, hidden: false });
  const meXpPoints = xpPointsForProfile(me);
  const meXpLevel = xpLevelInfoForProfile(me);
  const meXpReward = xpRewardForProfile(me);

  const groups = conversationGroups || { inbox: [], archived: [], hidden: [] };

  useEffect(() => {
    if (!menu) return undefined;
    function closeMenu() {
      setMenu(null);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setMenu(null);
    }
    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", closeMenu);
    return () => {
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", closeMenu);
    };
  }, [menu]);

  const menuStyle = useMemo(() => {
    if (!menu) return null;
    const viewportWidth = typeof window !== "undefined" ? window.innerWidth : menu.x;
    const viewportHeight = typeof window !== "undefined" ? window.innerHeight : menu.y;
    const menuWidth = 320;
    const estimatedMenuHeight = 420;
    return {
      left: Math.max(12, Math.min(menu.x, viewportWidth - menuWidth - 12)),
      top: Math.max(12, Math.min(menu.y, viewportHeight - estimatedMenuHeight)),
    };
  }, [menu]);

  const menuMembers = useMemo(() => {
    if (!menu?.conversation) return [];
    return (menu.conversation.memberIds || []).map((uid) => menuMemberDetails(uid, me, profilesByUid));
  }, [menu, me, profilesByUid]);
  const canManageMenuGroup = !!(menu?.conversation
    && menu.conversation.type === "group"
    && (menu.conversation.createdBy === me.uid || canAdminManageGroups));
  const canRemoveFromMenuGroup = !!(menu?.conversation
    && menu.conversation.type === "group"
    && (menu.conversation.createdBy === me.uid || canAdminManageGroups || canModerateMembers));
  const canLeaveMenuGroup = !!(menu?.conversation
    && menu.conversation.type === "group"
    && (menu.conversation.memberIds || []).includes(me.uid));

  function toggleSection(key) {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function openMenu(event, conversation) {
    event.preventDefault();
    setMenu({ conversation, x: event.clientX, y: event.clientY });
  }

  function runMenuAction(action) {
    if (!menu) return;
    const conversation = menu.conversation;
    const pref = (conversationPrefsById && conversationPrefsById[conversation.id]) || {};
    setMenu(null);
    if (action === "add") {
      onAddPeople && onAddPeople(conversation);
      return;
    }
    if (action === "archive") {
      onArchiveConversation && onArchiveConversation(conversation.id, !pref.archived);
      return;
    }
    if (action === "hide") {
      onHideConversation && onHideConversation(conversation.id, !pref.hidden);
      return;
    }
    if (action === "leave") {
      onLeaveGroup && onLeaveGroup(conversation);
    }
  }

  function runMemberAction(action, targetUid) {
    if (!menu || !targetUid) return;
    const conversation = menu.conversation;
    setMenu(null);
    if (action === "remove") {
      onRemoveMember && onRemoveMember(conversation, targetUid);
      return;
    }
    if (action === "owner") {
      onTransferOwnership && onTransferOwnership(conversation, targetUid);
      return;
    }
    if (action === "mod-on") {
      onSetMemberRole && onSetMemberRole(targetUid, "moderator");
      return;
    }
    if (action === "helper-on") {
      onSetMemberRole && onSetMemberRole(targetUid, "helper_mod");
      return;
    }
    if (action === "mod-off") {
      onSetMemberRole && onSetMemberRole(targetUid, "member");
      return;
    }
    if (action === "clown-on") {
      onSetClownRole && onSetClownRole(targetUid, true);
      return;
    }
    if (action === "clown-off") {
      onSetClownRole && onSetClownRole(targetUid, false);
    }
  }

  function renderConversationRow(c) {
    const { title, subtitle, avatarText, avatarBg, status } = display(c, me.uid, profilesByUid);
    const isActive = c.id === activeId;
    const pref = conversationPrefsById?.[c.id] || {};
    const lastMessageMs = tsMs(c.lastMessage?.createdAt);
    const lastReadMs = tsMs(pref.lastReadAt);
    const hasUnread = !!lastMessageMs
      && c.lastMessage?.senderId !== me.uid
      && (!lastReadMs || lastReadMs < lastMessageMs);
    const hasMention = hasUnread && messageMentionsProfile(c.lastMessage, me);
    return (
      <button
        key={c.id}
        className={"chat-conv-row" + (isActive ? " active" : "")}
        onClick={() => {
          setMenu(null);
          onPick(c.id);
        }}
        onContextMenu={(event) => openMenu(event, c)}
      >
        <div className="chat-avatar-wrap">
          <div className="chat-avatar" style={{ background: avatarBg }} aria-hidden="true">
            {avatarText}
          </div>
          {c.type !== "group" && <span className={`chat-presence-dot ${status}`} />}
        </div>
        <div className="chat-conv-meta">
          <div className="chat-conv-line">
            <div className="chat-conv-title-wrap">
              <div className="chat-conv-title">{title}</div>
              {hasUnread && (
                <span className={"chat-conv-badge" + (hasMention ? " mention" : "")}>
                  {hasMention ? "@" : "New"}
                </span>
              )}
            </div>
            <div className="chat-conv-time">{c.lastMessage ? relativeTime(c.lastMessage.createdAt) : ""}</div>
          </div>
          <div className="chat-conv-line">
            <div className={"chat-conv-preview" + (hasMention ? " mention" : "") }>
              {c.lastMessage
                ? c.lastMessage.body
                : <span className="chat-muted">{subtitle || "No messages yet"}</span>}
            </div>
          </div>
        </div>
      </button>
    );
  }

  return (
    <aside className="chat-sidebar">
      <div className="chat-sidebar-header">
        <div className="chat-avatar-wrap">
          <div
            className="chat-avatar lg"
            style={{ background: avatarColor(me.uid) }}
            aria-hidden="true"
          >
            {avatarInitial(me.displayName)}
          </div>
          <span className={`chat-presence-dot ${statusMode || "online"}`} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="chat-sidebar-me-line">
            <div className="chat-sidebar-me">{me.displayName}</div>
            <span className="chat-sidebar-xp">{meXpPoints} XP</span>
            <span className={`chat-xp-badge chat-sidebar-rank ${meXpReward.key}`}>
              L{meXpLevel.level} {meXpReward.label}
            </span>
          </div>
          <div className="chat-sidebar-me-sub">
            {me.email || (me.loginName ? `@${me.loginName}` : "Chat account")}
          </div>
        </div>
        {showCollapseControl && (
          <button
            className="chat-btn-icon chat-sidebar-collapse-btn"
            title="Hide chats"
            onClick={onCollapseSidebar}
            aria-label="Hide chats"
          >
            ←
          </button>
        )}
        <button className="chat-btn-icon" title="Sign out" onClick={onSignOut} aria-label="Sign out">
          ↩
        </button>
      </div>

      <div className="chat-primary-actions">
        <button className="chat-new-btn" onClick={onNew} disabled={newChatDisabled} title={newChatTitle || "New chat"}>
          <span style={{ fontSize: "1.15rem", lineHeight: 1 }}>+</span> New chat
        </button>
      </div>
      <div className="chat-conv-list">
        <div className="chat-section-label">{sectionLabel}</div>
        {groups.inbox.length === 0 && groups.archived.length === 0 && groups.hidden.length === 0 && (
          <div className="chat-conv-empty">
            {sectionLabel === "Channels"
              ? "No channels here yet."
              : <><span>No chats yet. Click </span><strong>+ New chat</strong><span> to start one.</span></>}
          </div>
        )}
        {groups.inbox.map(renderConversationRow)}

        {groups.archived.length > 0 && (
          <>
            <button
              type="button"
              className="chat-section-toggle"
              onClick={() => toggleSection("archived")}
            >
              <span>Archived</span>
              <strong>{groups.archived.length}</strong>
            </button>
            {expandedSections.archived && groups.archived.map(renderConversationRow)}
          </>
        )}

        {groups.hidden.length > 0 && (
          <>
            <button
              type="button"
              className="chat-section-toggle"
              onClick={() => toggleSection("hidden")}
            >
              <span>Hidden</span>
              <strong>{groups.hidden.length}</strong>
            </button>
            {expandedSections.hidden && groups.hidden.map(renderConversationRow)}
          </>
        )}
      </div>

      {menu && menuStyle && (
        <div
          className="chat-conv-menu"
          style={{ left: menuStyle.left, top: menuStyle.top }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="chat-conv-menu-section">
            <div className="chat-conv-menu-label">
              {menu.conversation.type === "group" ? "People in chat" : "Chat members"}
            </div>
            <div className="chat-conv-menu-members">
              {menuMembers.map((member) => (
                <div key={member.uid} className="chat-conv-menu-member">
                  <div className="chat-conv-menu-member-head">
                    <div className="chat-conv-menu-member-name">
                      <span>{member.displayName}</span>
                      {member.isAdmin && (
                        <span className="chat-conv-menu-member-tag admin">Admin Vivaan</span>
                      )}
                      {member.isModerator && (
                        <span className={"chat-conv-menu-member-tag " + (member.isHelperModerator ? "helper-moderator" : "moderator")}>
                          {member.isHelperModerator ? "Helper mod" : "Respected moderator"}
                        </span>
                      )}
                      {member.isClown && (
                        <span className="chat-conv-menu-member-tag clown">Clown</span>
                      )}
                      {menu.conversation.createdBy === member.uid && (
                        <span className="chat-conv-menu-member-tag owner">Owner</span>
                      )}
                      {member.isMe && <span className="chat-conv-menu-member-tag">You</span>}
                    </div>
                    <div className="chat-conv-menu-member-actions">
                      {menu.conversation.type === "group" && canManageMenuGroup && menu.conversation.createdBy !== member.uid && (
                        <button
                          type="button"
                          className="chat-conv-menu-member-btn"
                          onClick={() => runMemberAction("owner", member.uid)}
                        >
                          Make owner
                        </button>
                      )}
                      {menu.conversation.type === "group" && canRemoveFromMenuGroup && !member.isMe && menu.conversation.createdBy !== member.uid && (
                        <button
                          type="button"
                          className="chat-conv-menu-member-btn danger"
                          onClick={() => runMemberAction("remove", member.uid)}
                        >
                          Remove
                        </button>
                      )}
                      {canAssignModerators && !member.isMe && !member.isAdmin && (
                        <>
                          {!member.isFullModerator && (
                            <button
                              type="button"
                              className="chat-conv-menu-member-btn moderator"
                              onClick={() => runMemberAction("mod-on", member.uid)}
                            >
                              Make mod
                            </button>
                          )}
                          {!member.isHelperModerator && (
                            <button
                              type="button"
                              className="chat-conv-menu-member-btn helper-moderator"
                              onClick={() => runMemberAction("helper-on", member.uid)}
                            >
                              Make helper mod
                            </button>
                          )}
                          {member.isModerator && (
                            <button
                              type="button"
                              className="chat-conv-menu-member-btn moderator"
                              onClick={() => runMemberAction("mod-off", member.uid)}
                            >
                              Remove mod role
                            </button>
                          )}
                          <button
                            type="button"
                            className="chat-conv-menu-member-btn clown"
                            onClick={() => runMemberAction(member.isClown ? "clown-off" : "clown-on", member.uid)}
                          >
                            {member.isClown ? "Remove clown" : "Make clown"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="chat-conv-menu-member-email">{member.email}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="chat-conv-menu-divider" />
          {menu.conversation.type === "group" && (
            <button
              type="button"
              className="chat-conv-menu-item"
              onClick={() => runMenuAction("add")}
              disabled={(menu.conversation.memberIds || []).length >= MAX_GROUP_MEMBERS}
            >
              Add people
            </button>
          )}
          {canLeaveMenuGroup && (
            <button
              type="button"
              className="chat-conv-menu-item"
              onClick={() => runMenuAction("leave")}
            >
              Leave group
            </button>
          )}
          <button
            type="button"
            className="chat-conv-menu-item"
            onClick={() => runMenuAction("archive")}
          >
            {conversationPrefsById?.[menu.conversation.id]?.archived ? "Move to inbox" : "Archive chat"}
          </button>
          <button
            type="button"
            className="chat-conv-menu-item"
            onClick={() => runMenuAction("hide")}
          >
            {conversationPrefsById?.[menu.conversation.id]?.hidden ? "Unhide chat" : "Hide chat"}
          </button>
        </div>
      )}
    </aside>
  );
}
